/**
 * LANDING PAGE INDUSTRY DATA
 * ==========================
 * 
 * This file extends the base industry data from platformStats.js
 * with marketing-specific metadata (stakeholders, metrics, data sources)
 * for use on the landing page and enquiry forms.
 * 
 * SINGLE SOURCE OF TRUTH:
 * - Base industry list: platformStats.js → SUPPORTED_INDUSTRIES
 * - Marketing metadata: This file → INDUSTRY_MARKETING_DATA
 */

import { SUPPORTED_INDUSTRIES } from './platformStats';
import { INDUSTRY_MARKETING_DATA } from './_industry_marketing_data';
import {
  Users, Utensils, ShoppingBag, Heart, Gamepad2, Home, Briefcase, Dumbbell,
  Car, Scissors, Music, GraduationCap, Package, Hammer, Sparkles, Shield,
  Globe, Truck, Wrench, Scale, Hotel, Leaf, Wheat, Landmark
} from 'lucide-react';

// Lucide icon mapping for each industry
const INDUSTRY_ICONS = {
  crm: Users,
  hospitality: Utensils,
  retail: ShoppingBag,
  healthcare: Heart,
  professional: Briefcase,
  fitness: Dumbbell,
  property: Home,
  construction: Hammer,
  automotive: Car,
  education: GraduationCap,
  beauty: Scissors,
  manufacturing: Package,
  entertainment: Music,
  technology: Globe,
  finance: Landmark,
  agriculture: Wheat,
  logistics: Truck,
  trades: Wrench,
  legal: Scale,
  accommodation: Hotel,
  gaming: Gamepad2,
  environmental: Leaf,
  cleaning: Sparkles,
  pet_services: Heart,
  childcare: Users,
  senior_care: Home,
  pharmacy: Package,
  staffing: Users,
  printing: Package,
  pest_control: Shield,
  moving: Truck,
};


// Additional industries for M&A/Investment (shown on landing page but not in enquiry form)
const INVESTMENT_INDUSTRIES = {
  venture_capital: {
    id: 'venture_capital',
    name: 'Venture Capital & Private Equity',
    icon: '📈',
    lucideIcon: Landmark,
    description: 'VC firms, PE, angel investors',
    popular: false,
    stakeholders: ['Partners', 'Associates', 'Portfolio Companies', 'LPs', 'Advisors', 'Board Members'],
    metrics: ['Deal flow', 'Pipeline value', 'IRR', 'MOIC', 'Portfolio performance', 'Win rate'],
    dataSources: ['PitchBook', 'Crunchbase', 'Company financials', 'Market intelligence', 'LP reporting']
  },
  business_brokers: {
    id: 'business_brokers',
    name: 'Business Brokers & M&A',
    icon: '🤝',
    lucideIcon: Briefcase,
    description: 'Business sales, M&A advisory',
    popular: false,
    stakeholders: ['Brokers', 'Sellers', 'Buyers', 'Advisors', 'Lawyers', 'Accountants'],
    metrics: ['Listings', 'Settlement rate', 'Time to sell', 'Deal value', 'Commission', 'Pipeline'],
    dataSources: ['AIBB', 'Business sales data', 'Valuations', 'Due diligence', 'Market comparables']
  }
};

/**
 * Generate complete industry objects by merging base data with marketing metadata
 */
const generateIndustryObject = (id) => {
  const base = SUPPORTED_INDUSTRIES[id];
  const marketing = INDUSTRY_MARKETING_DATA[id];
  const icon = INDUSTRY_ICONS[id];
  
  if (!base || !marketing) return null;
  
  return {
    id,
    name: base.name,
    icon: base.icon,
    lucideIcon: icon,
    color: base.color,
    hasSubVerticals: base.hasSubVerticals || false,
    ...marketing
  };
};

/**
 * Get all industries as an array for the landing page
 * Includes investment industries at the end
 */
export const getLandingPageIndustries = () => {
  const industries = Object.keys(SUPPORTED_INDUSTRIES)
    .map(generateIndustryObject)
    .filter(Boolean);
  
  // Add investment industries
  industries.push({
    ...INVESTMENT_INDUSTRIES.venture_capital,
  });
  industries.push({
    ...INVESTMENT_INDUSTRIES.business_brokers,
  });
  
  return industries;
};

/**
 * Get industries for the enquiry form
 * Excludes investment industries
 */
export const getEnquiryFormIndustries = () => {
  return Object.keys(SUPPORTED_INDUSTRIES)
    .map(generateIndustryObject)
    .filter(Boolean);
};

/**
 * Get popular industries only
 */
export const getPopularIndustries = () => {
  return Object.keys(SUPPORTED_INDUSTRIES)
    .map(generateIndustryObject)
    .filter(ind => ind && ind.popular);
};

/**
 * Get non-popular industries
 */
export const getNonPopularIndustries = () => {
  return Object.keys(SUPPORTED_INDUSTRIES)
    .map(generateIndustryObject)
    .filter(ind => ind && !ind.popular);
};

/**
 * Get a single industry by ID
 */
export const getIndustryById = (id) => {
  return generateIndustryObject(id);
};

// Extended industries list for "Other" selection in enquiry form
export const EXTENDED_INDUSTRIES = [
  'Insurance Services', 'Telecommunications',
  'Energy & Utilities', 'Mining & Resources', 'Media & Publishing',
  'Advertising & Marketing', 'Travel & Tourism', 'Aviation',
  'Marine & Maritime', 'Government & Public Sector', 'Non-Profit & Charity',
  'Sports & Recreation', 'Security Services',
  'Waste Management', 'Architecture & Design', 'Engineering Services', 
  'Research & Development', 'Laboratory Services', 'Veterinary Services',
  'Mental Health Services', 'Pharmaceutical', 'Medical Devices', 'Biotechnology',
  'Food Production', 'Beverage Manufacturing', 'Textiles & Apparel',
  'Furniture & Homewares', 'Electronics Manufacturing', 'Chemical Manufacturing'
];

export default {
  getLandingPageIndustries,
  getEnquiryFormIndustries,
  getPopularIndustries,
  getNonPopularIndustries,
  getIndustryById,
  EXTENDED_INDUSTRIES,
  INDUSTRY_ICONS
};
